import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { PractitionerData, useGetPractitioner } from 'api';
import AuthContext, { User } from 'contexts/AuthContext';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import { Role } from 'types/types';
import { BetaContext } from '@insights-ltd/design-library/components';
import { UserPermissions } from 'permissions';
import { BETA_ROLE_MAP, ROLE_MAP } from './roleToPermissions';

// This is needed to cover the state where the data fetch is successful but the
// useEffect has not run yet.
const waitingForRerender = (status: string, user?: PractitionerData) =>
  status === 'success' && !user;

export const getUserWithPermissions = (
  user: PractitionerData,
  roleMap: Record<Role, UserPermissions>,
): User => {
  const userRole = user.role ?? 'STANDARD';
  return {
    ...user,
    role: userRole,
    permissions: roleMap[userRole] || roleMap.STANDARD,
  };
};

const AuthenticationProvider = ({ children }: any) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | undefined>();
  const { status, error, data } = useGetPractitioner();
  const { betaEnabled } = useContext(BetaContext) ?? { betaEnabled: false };
  const value = useMemo(() => {
    if (status === 'success') {
      return { user };
    }

    return {};
  }, [status, user]);

  React.useEffect(() => {
    if (!error && data) {
      setUser(
        getUserWithPermissions(data, betaEnabled ? BETA_ROLE_MAP : ROLE_MAP),
      );
      datadogRum.setUser({ id: data.id });
    }
  }, [betaEnabled, data, error]);

  if (status === 'pending' || waitingForRerender(status, user)) {
    return (
      <FullScreenSpinner
        message={t('ui.event-management.authentication.loading')}
      />
    );
  }

  // Ignore unauthenticated users as they will fall into Unauthenticated Routes
  if (status === 'error' && error?.message !== '401') {
    return (
      <FullScreenError
        message={t('ui.event-management.authentication.error')}
      />
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthenticationProvider;
